<template>
  <main class="security">
    <!-- Header -->
    <div class="security__header">
      <h1 class="security__title">Login & Security</h1>
      <p class="security__subtitle">Manage your account security settings</p>
    </div>

    <!-- Security Options -->
    <div class="security__options">
      <!-- Change PIN -->
      <button class="security__option" @click="showPinModal = true">
        <div class="security__option-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 12.5V14.1667M6.66667 17.5H13.3333C14.2538 17.5 15 16.7538 15 15.8333V10.8333C15 9.91286 14.2538 9.16667 13.3333 9.16667H6.66667C5.74619 9.16667 5 9.91286 5 10.8333V15.8333C5 16.7538 5.74619 17.5 6.66667 17.5ZM12.5 9.16667V5.83333C12.5 4.45262 11.3807 3.33333 10 3.33333C8.61929 3.33333 7.5 4.45262 7.5 5.83333V9.16667H12.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="security__option-content">
          <span class="security__option-title">Change PIN</span>
          <span class="security__option-description">Update your 4-digit transaction PIN</span>
        </div>
        <svg class="security__option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Reset Password -->
      <button class="security__option" @click="showPasswordModal = true">
        <div class="security__option-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5.83333 9.16667V5.83333C5.83333 4.72826 6.27232 3.66846 7.05372 2.88706C7.83512 2.10565 8.89493 1.66667 10 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1667 4.72826 14.1667 5.83333V9.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="security__option-content">
          <span class="security__option-title">Reset Password</span>
          <span class="security__option-description">Change your account login password</span>
        </div>
        <svg class="security__option-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Two-Factor Authentication -->
      <button class="security__option" @click="showTwoFaModal = !showTwoFaModal">
        <div class="security__option-icon security__option-icon--shield">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 18.3333C10 18.3333 16.6667 15 16.6667 10V4.16667L10 1.66667L3.33334 4.16667V10C3.33334 15 10 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="security__option-content">
          <span class="security__option-title">Two-Factor Authentication</span>
          <span class="security__option-description">Add extra security with 2FA</span>
        </div>
        <div class="security__toggle" :class="{ 'security__toggle--active': is2faActive }">
          <div class="security__toggle-dot"></div>
        </div>
      </button>
    </div>

    <!-- Modals -->
    <ChangePin v-if="showPinModal" @close="showPinModal = false" />
    <ResetPassword v-if="showPasswordModal" @close="showPasswordModal = false" />
    
    <TwoFaModal
      v-if="showTwoFaModal"
      title="Two-Factor Authentication"
      :subTitle="twoFaSubTitle"
      :form="twoFaForm"
      @goBack="goBack"
      @close="showTwoFaModal = !showTwoFaModal"
      @mounted="refresh"
    >
      <template v-slot:content>
        <PinConfirmation
          v-if="twoFaForm === 'pinForm'"
          class="tw-px-4 lg:tw-px-12 tw-mt-12"
          btnTitle="Continue"
          :showHeader="false"
          @confirmed="confirmed"
          :activeStatus="is2faActive"
          @deactivated="deactivated"
        />
        <TwoFaInfoForm
          v-if="twoFaForm === 'twoFaInfoForm'"
          class="tw-px-4 lg:tw-px-12 tw-mt-12"
          header="Two-Factor Authentication (2FA)"
          text="2FA adds an extra layer of security for your account by generating a secure code on your device for approving transactions."
          @continue="gotoInstallInfo"
        />
        <TwoFaInfoForm
          v-if="twoFaForm === 'installInfoForm'"
          class="tw-px-4 lg:tw-px-12 tw-mt-12"
          header="Setup Authenticator App"
          text="An Authenticator App is required to setup two-factor. Download & Setup 'Google Authenticator' on your phone to continue."
          @continue="gotoQrCode"
        />
        <GenerateQrForm
          v-if="twoFaForm === 'generateQrForm'"
          class="tw-px-4 lg:tw-px-12 tw-mt-12"
          @continue="gotoQrDetails"
        />
        <CodeDetails
          v-if="twoFaForm === 'codeDetails'"
          class="tw-px-4 lg:tw-px-12 tw-mt-12"
          @continue="twoFaPin"
        />
        <TwoFaPin
          v-if="twoFaForm === 'twoFaPin'"
          class="tw-px-4 lg:tw-px-12 tw-mt-12"
          @close="showTwoFaModal = !showTwoFaModal"
        />
      </template>
    </TwoFaModal>
  </main>
</template>

<script>
import ChangePin from "@/components/account/ChangePin";
import ResetPassword from "@/components/account/ResetPassword";
import TwoFaModal from "@/components/general/sidemodal/SideModal";
import TwoFaInfoForm from "@/components/account/security/TwoFaInfoForm";
import GenerateQrForm from "@/components/account/security/GenerateQrForm";
import CodeDetails from "@/components/account/security/CodeDetails.vue";
import TwoFaPin from "@/components/account/security/TwoFaPin";
import { mapState, mapActions } from "vuex";
import PinConfirmation from "@/components/general/PinConfirmation";

export default {
  name: "Security",
  data() {
    return {
      showPinModal: false,
      showPasswordModal: false,
      showTwoFaModal: false,
      twoFaForm: "",
      twoFaSubTitle: "",
    };
  },
  components: {
    ChangePin,
    ResetPassword,
    TwoFaInfoForm,
    GenerateQrForm,
    CodeDetails,
    TwoFaPin,
    TwoFaModal,
    PinConfirmation,
  },
  computed: {
    ...mapState({
      organisationDetails: (state) =>
        state.organisationModule.organisationDetails,
      teamMember: (state) => state.organisationModule.teamMember,
    }),
    is2faActive() {
      if (this.teamMember) {
        return this.teamMember?.two_factor_enabled || false;
      } else {
        return this.organisationDetails.two_factor_enabled || false;
      }
    },
  },
  created() {
    this.getTeamMemberInfo();
  },
  methods: {
    ...mapActions("organisationModule", ["getTeamMemberInfo"]),
    goBack(form) {
      switch (form) {
        case "createPinForm":
          this.pinForm = "passwordForm";
          this.pinSubTitle = "Enter your account password";
          break;
        case "confirmPinForm":
          this.pinForm = "createPinForm";
          this.pinSubTitle = "Set New 4-digit PIN";
          break;
        case "twoFaInfoForm":
          this.twoFaForm = "pinForm";
          this.twoFaSubTitle = "Enter your PIN to continue";
          break;
        case "installInfoForm":
          this.twoFaForm = "twoFaInfoForm";
          this.twoFaSubTitle = "";
          break;
        case "generateQrForm":
          this.twoFaForm = "installInfoForm";
          this.twoFaSubTitle = "";
          break;
        case "codeDetails":
          this.twoFaForm = "generateQrForm";
          this.twoFaSubTitle = "";
          break;
        case "twoFaPin":
          this.twoFaForm = "codeDetails";
          this.twoFaSubTitle = "";
          break;

        default:
          break;
      }
    },
    confirmed() {
      this.twoFaForm = "twoFaInfoForm";
      this.twoFaSubTitle = "";
    },

    deactivated() {
      this.showTwoFaModal = false;
      this.twoFaForm = "";
      this.twoFaSubTitle = "";
    },

    gotoInstallInfo() {
      this.twoFaForm = "installInfoForm";
      this.twoFaSubTitle = "";
    },

    gotoQrCode() {
      this.twoFaForm = "generateQrForm";
      this.twoFaSubTitle = "";
    },

    gotoQrDetails() {
      this.twoFaForm = "codeDetails";
      this.twoFaSubTitle = "";
    },

    twoFaPin() {
      this.twoFaForm = "twoFaPin";
      this.twoFaSubTitle = "Enter your authentication PIN";
    },

    refresh(modal) {
      switch (modal) {
        case "Change Pin":
          this.payload.password = "";
          this.pinForm = "passwordForm";
          this.pinSubTitle = "Enter your account password";
          break;
        case "Two-Factor Authentication":
          this.twoFaForm = "pinForm";
          this.twoFaSubTitle = "Enter your PIN to continue";
          break;

        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.security {
  &__header {
    padding: 24px;
    border-bottom: 1px solid #E2E8F0;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__options {
    padding: 8px 0;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 20px 24px;
    background: transparent;
    border: none;
    border-bottom: 1px solid #F1F5F9;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;

    &:hover {
      background: #F8FAFC;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: #F1F5F9;
    border-radius: 10px;
    color: #64748B;
    flex-shrink: 0;

    &--shield {
      background: #EEF4FF;
      color: #4D84FF;
    }
  }

  &__option-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__option-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
  }

  &__option-description {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;
  }

  &__option-arrow {
    color: #94A3B8;
    flex-shrink: 0;
  }

  &__toggle {
    width: 44px;
    height: 24px;
    background: #E2E8F0;
    border-radius: 12px;
    padding: 2px;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &--active {
      background: #4D84FF;

      .security__toggle-dot {
        transform: translateX(20px);
      }
    }
  }

  &__toggle-dot {
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
  }
}
</style>
